import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtClock, fmtDuration, todayWIB, StatusPill } from "@/components/staff/attendance/utils";

const Stat = ({ label, value, testid }) => (
  <div className="card-paper p-4"><div className="text-xs text-muted2">{label}</div><div className="text-2xl font-bold mt-1" data-testid={testid}>{value}</div></div>
);

const ShiftBlock = ({ shift, records, absent }) => (
  <div className="card-paper p-5" data-testid={`attendance-shift-${shift.id}`}>
    <div className="flex items-center justify-between">
      <h4 className="font-semibold">Shift {shift.label} <span className="text-muted2 font-normal text-sm">{shift.start}–{shift.end}</span></h4>
      <span className="text-xs text-muted2">{records.length} hadir · {absent.length} belum</span>
    </div>
    <div className="divide-y divide-sage-200 mt-2 text-sm">
      {records.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`attendance-record-${r.id}`}>
          <div><div className="font-semibold">{r.name}</div><div className="text-xs text-muted2">{r.division || "-"}{(r.swap || (r.assigned_shift && r.assigned_shift !== r.shift)) && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold" data-testid={`attendance-swap-badge-${r.id}`}>tukar shift</span>}</div></div>
          <span className="text-muted2">{fmtClock(r.clock_in)} – {r.clock_out ? `${fmtClock(r.clock_out)} (${fmtDuration(r.duration_minutes)})` : "masih bekerja"}</span>
          <StatusPill rec={r} />
        </div>
      ))}
      {absent.map((a) => (
        <div key={a.user_id} className="flex items-center justify-between gap-2 py-2.5 opacity-70" data-testid={`attendance-absent-${a.user_id}`}>
          <div><div className="font-semibold">{a.name}</div><div className="text-xs text-muted2">{a.division || "-"}</div></div>
          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">Belum absen</span>
        </div>
      ))}
      {records.length + absent.length === 0 && <p className="py-3 text-muted2">Tidak ada karyawan di shift ini.</p>}
    </div>
  </div>
);

export const AttendanceReport = ({ config }) => {
  const [date, setDate] = useState(todayWIB());
  const [division, setDivision] = useState("all");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    const params = { date, division: division === "all" ? "" : division };
    api.get("/admin/attendance", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [date, division]);
  useEffect(() => { load(); }, [load]);

  const recs = data?.records || [];
  const absent = data?.absent || [];
  return (
    <div className="mt-10" data-testid="attendance-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-serif font-semibold text-2xl">Rekap absensi karyawan</h3>
        <div className="flex flex-wrap gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm" data-testid="attendance-date-input" />
          <Select value={division} onValueChange={setDivision}>
            <SelectTrigger className="w-48 bg-paper" data-testid="attendance-division-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua divisi</SelectItem>
              {config.divisions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4">
        <Stat label="Hadir" value={recs.length} testid="attendance-stat-present" />
        <Stat label="Terlambat" value={recs.filter((r) => r.status === "terlambat").length} testid="attendance-stat-late" />
        <Stat label="Belum absen" value={absent.length} testid="attendance-stat-absent" />
      </div>
      <div className="grid xl:grid-cols-3 gap-4 mt-4">
        {config.shifts.map((s) => (
          <ShiftBlock key={s.id} shift={s} records={recs.filter((r) => r.shift === s.id)} absent={absent.filter((a) => a.shift === s.id)} />
        ))}
      </div>
    </div>
  );
};
